<template>
  <li
    class="subscription-item"
    :class="{
      'loading': loading,
      'disabled': disabled,
      'unfollowed': !subscription.isActive,
    }"
    @click="$emit('click', subscription)"
  >
    <subscription-icon
      :color-image="colorImage"
      :subscription-name="subscription.name"
      :unfollowed="!subscription.isActive"
      :loading="loading"
      :disabled="disabled"
    />
    <div class="content">
      <span class="label" :class="{ 'loading': loading }">{{ subscriptionLabel }}</span>
      <span class="periodicity" :class="{ 'loading': loading }">{{ writeOffVariant }}</span>
      <span v-if="isShownNextPay" class="next-pay">
        <cs-icon :color="colorImage" name="calendar" container-size="extra-small" />
        <span class="next-pay-text">{{ nextPay }}</span>
      </span>
    </div>
    <div class="sum" :class="{ 'loading': loading }">
      {{ displayOverallAmount }}
    </div>
    <unsubscribe-button
      :active="subscription.isActive"
      :loading="loading"
      :disabled="disabled"
      @click="onClickUnfollow"
    />
  </li>
</template>

<script>
import { mapGetters } from 'vuex';
import { isMobile } from 'mobile-device-detect';

import { formatAmount } from 'lib/utils/amount';

import SubscriptionIcon from '@/pages/subscriptions/components/SubscriptionIcon';
import UnsubscribeButton from '@/pages/subscriptions/components/UnsubscribeButton';

import formatAccountNumber from '@/components/main/format-account-number';

const PERIODS_MAP = {
  DAILY: 'common.day',
  WEEKLY: 'common.short.week',
  MONTHLY: 'common.short.month',
  ['SEMI-ANNUALLY']: 'common.short.semi-annually',
  ANNUALLY: 'common.short.annually',
};

export default {
  name: 'SubscriptionItem',
  components: {
    UnsubscribeButton,
    SubscriptionIcon,
  },
  props: {
    subscription: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    subscriptionLabel() {
      return this.subscription.name || formatAccountNumber(this.subscription.accountNumber);
    },
    billTariff() {
      return this.subscription.billTariff;
    },
    writeOffVariant() {
      const per = this.billTariff.subscription.per;
      const amount = this.billTariff.amount;
      const { currency } = this.subscription;

      if (this.$validate.isNull(per)) {
        return this.$t('on.request');
      }

      return `${formatAmount(amount, currency)} / ${this.$t(PERIODS_MAP[per])}`;
    },
    displayOverallAmount() {
      return formatAmount(this.subscription.overallAmount, {
        significant_zeros: this.subscription.currency.significant_zeros,
        type: this.subscription.currency.type,
      });
    },
    isShownNextPay() {
      const { isActive, nextDeliverAt } = this.subscription;

      if (this.loading) {
        return false;
      }

      return isActive && nextDeliverAt;
    },
    nextPay() {
      if (isMobile) {
        return this.$t('next.payment.abbreviated', {
          displayDate: `${this.subscription.nextDeliverAt.substring(0, 6)}${this.subscription.nextDeliverAt.slice(-2)}`,
        });
      }

      return this.$t('next.payment.full', {
        displayDate: this.subscription.nextDeliverAt,
      });
    },
    colorImage() {
      if (!this.isObsidianTheme && this.loading) {
        return 'transparent';
      }

      if (this.isObsidianTheme && this.disabled) {
        return '#DADADA';
      }

      return '#fff';
    },
    ...mapGetters(['isObsidianTheme']),
  },
  watch: {
    subscription: {
      deep: true,
      handler() {
        this.loading = false;
      },
    },
  },
  methods: {
    async onClickUnfollow() {
      if (this.loading || !this.subscription.isActive) {
        return;
      }

      this.$emit('unfollow', this.subscription.id);
    },
  },
};
</script>

<style lang="sass" scoped>
  .subscription-item
    position: relative
    display: flex
    margin: 10px 0
    padding: 17px 19px
    border-radius: 5px
    border: 1px solid transparent
    box-shadow: 0px 0px 7px rgba(0, 0, 0, 0.15)
    cursor: pointer

    #{theme("eclipse")} &
      background-color: #fff

    #{theme("obsidian")} &
      background-color: #27283C

    &:hover
      #{theme("eclipse")} &
        background-color: #fff

      #{theme("obsidian")} &
        background-color: #31334D

    &.disabled
      cursor: not-allowed

      #{theme("eclipse")} &
        color: #999999
        background-color: #fff

      #{theme("obsidian")} &
        color: #41404F
        background-color: #1F1E30

      &:hover
        #{theme("eclipse")} &
          background-color: #fff

        #{theme("obsidian")} &
          background-color: #1F1E30

    &.loading
      color: transparent
      background-size: 400% 400%
      animation: loading 0.7s ease infinite
      cursor: wait

      #{theme("eclipse")} &
        background-color: #fff
        background-image: linear-gradient(90deg, #0000 0%, #ccc 100%)

      #{theme("obsidian")} &
        background-color: #1F1E30
        background-image: linear-gradient(90deg, #0000 0%, #31334C 100%)

      &.hover,
      &:hover
        #{theme("eclipse")} &
          background-color: #fff
          background-image: linear-gradient(90deg, #0000 0%, #eee 100%)

        #{theme("obsidian")} &
          background-color: #1F1E30
          background-image: linear-gradient(90deg, #0000 0%, #3A3949 100%)

  .content
    flex: 2
    display: flex
    flex-direction: column
    margin-left: 20px

    .label
      margin-bottom: 3px
      font-weight: 600
      font-size: 14px
      line-height: 20px
      color: #fff
      opacity: .75

      &.loading
        width: 125px
        height: 10px
        margin-bottom: 15px
        border-radius: 5px
        overflow: hidden
        opacity: 0.1

        #{theme("obsidian")} &
          background: #fff

        #{theme("eclipse")} &
          background: #212121

    .periodicity
      font-weight: 300
      font-size: 14px
      line-height: 20px

      &.loading
        width: 125px
        height: 15px
        border-radius: 5px
        overflow: hidden
        opacity: 0.1

        #{theme("obsidian")} &
          background: #fff

        #{theme("eclipse")} &
          background: #212121

    .next-pay
      margin-top: 3px
      display: flex
      font-weight: 300
      font-size: 12px
      line-height: 18px
      opacity: 0.5

      .next-pay-text
        margin-left: 5px

  .sum
    flex: 1
    margin-left: auto
    font-weight: 600
    font-size: 18px
    line-height: 22px
    text-align: right

    &.loading
      width: 45px
      height: 10px
      flex: none
      overflow: hidden
      border-radius: 5px
      opacity: 0.1

      #{theme("obsidian")} &
        background: #fff

      #{theme("eclipse")} &
        background: #212121
</style>
